import { useErrorHandlingSlice } from 'app/error/slice';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import useApi from 'services/mavapi/useApi';
import {
  BacknumbersRange,
  BackNumbersWrapper,
  RiderService,
  DocumentImagesService,
} from 'services/openapi';
import type { DocumentImageList } from 'services/openapi/models/DocumentImageList';
import type { UpdateDocumentImageDisplayOption } from 'services/openapi/models/UpdateDocumentImageDisplayOption';

const useSettingsApi = () => {
  const [loading, setLoading] = useState(false);
  const [backNumberLoading, setBackNumberLoading] = useState(false);
  const [documentImageLoading, setDocumentImageLoading] = useState(false);
  const [uploadImageLoading, setUploadImageLoading] = useState(false);

  const {
    riderGetAllBackNumbers,
    riderMakeBackNumberAvailable,
    riderGetAllBackNumbersDetailed,
    riderCreateRangeOfBacknumber,
  } = RiderService;

  const {
    documentImagesUploadDocumentImages,
    documentImagesGetCommonDocumentImages,
    documentImagesDeleteDocumentImage,
    documentImagesUpdateDisplayOption,
  } = DocumentImagesService;

  const [, setError] = useState<unknown | null>();
  const { error: apiError, handleRequest } = useApi();
  const [backnumberList, setBacknumberList] = useState<BackNumbersWrapper>();
  const [allRiderByBackNumberList, setAllRiderByBackNumberList] =
    useState<BackNumbersWrapper>();
  const [isAddBackNumber, setIsAddBackNumber] = useState<boolean>(false);

  // Document Images State
  const [documentImageList, setDocumentImageList] = useState<DocumentImageList[]>([]);
  const [commonDocumentImageList, setCommonDocumentImageList] = useState<DocumentImageList[]>([]);

  const [isPopupLoading, setIsPopupLoading] = useState(false);
  const { actions } = useErrorHandlingSlice();
  const dispatch = useDispatch();

  // ============ BACK NUMBERS API CALLS ============

  const fetchAdminBacknumberList = (
    page?: number,
    limit?: number,
    searchInput?: string,
  ) => {
    setLoading(true);
    let pageNumCall = page ? Math.floor(page / (limit || 10)) + 1 : 1;
    handleRequest(riderGetAllBackNumbers(pageNumCall, limit, searchInput))
      .then(res => {
        if (res) {
          setBacknumberList(res);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const fetchAllRiderByBackNumber = (
    page?: number,
    limit?: number,
    searchInput?: string,
  ) => {
    setIsPopupLoading(true);
    let pageNumCall = page ? Math.floor(page / (limit || 10)) + 1 : 1;
    handleRequest(
      riderGetAllBackNumbersDetailed(pageNumCall, limit, searchInput),
    )
      .then(res => {
        if (res) {
          setAllRiderByBackNumberList(res);
        }
        setIsPopupLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setIsPopupLoading(false);
      });
  };

  const makeBacknumberAvailable = (
    backNumber?: string,
    OnSuccess?: () => void,
  ) => {
    setLoading(true);
    handleRequest(riderMakeBackNumberAvailable(backNumber))
      .then(res => {
        if (res) {
          OnSuccess?.();
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Back Number was made available Successfully',
              show: true,
              errorType: 'success',
            }),
          );
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const addRangeBackNumbers = (
    requestBody?: BacknumbersRange,
    OnSuccess?: () => void,
  ) => {
    setBackNumberLoading(true);
    handleRequest(riderCreateRangeOfBacknumber(requestBody))
      .then(res => {
        if (res) {
          if (res.code === 'ALL_ALLREADY_ADDED') {
            setIsAddBackNumber(true);
            dispatch(
              actions.dispatchToast({
                errorTxt: 'All back numbers are already added',
                show: true,
                errorType: 'error',
              }),
            );
          } else {
            setIsAddBackNumber(false);
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Back number added successfully',
                show: true,
                errorType: 'success',
              }),
            );
            OnSuccess?.();
          }
          setBackNumberLoading(false);
        }
      })
      .catch(() => {
        setError(apiError);
        setBackNumberLoading(false);
      });
  };

  // ============ DOCUMENT IMAGES API CALLS ============

  const uploadDocumentImages = (
    files: File[],
    displayOption?: number,
    OnSuccess?: () => void,
  ) => {
    setUploadImageLoading(true);
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    if (displayOption !== undefined) {
      formData.append('displayOption', displayOption.toString());
    }

    handleRequest(documentImagesUploadDocumentImages({ files, displayOption }))
      .then(res => {
        OnSuccess?.();
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Document images uploaded successfully',
            show: true,
            errorType: 'success',
          }),
        );
        setUploadImageLoading(false);
      })
      .catch(() => {
        setError(apiError);
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Failed to upload document images',
            show: true,
            errorType: 'error',
          }),
        );
        setUploadImageLoading(false);
      });
  };
  const fetchCommonDocumentImages = () => {
    setDocumentImageLoading(true);
    handleRequest(documentImagesGetCommonDocumentImages())
      .then(res => {
        if (res) {
          setCommonDocumentImageList(res);
        }
        setDocumentImageLoading(false);
      })
      .catch(() => {
        setError(apiError);
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Failed to fetch common document images',
            show: true,
            errorType: 'error',
          }),
        );
        setDocumentImageLoading(false);
      });
  };

  const deleteDocumentImage = (imageId: string, OnSuccess?: () => void) => {
    setDocumentImageLoading(true);
    handleRequest(documentImagesDeleteDocumentImage(imageId))
      .then(res => {
        if (res) {
          OnSuccess?.();
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Document image deleted successfully',
              show: true,
              errorType: 'success',
            }),
          );
        }
        setDocumentImageLoading(false);
      })
      .catch(() => {
        setError(apiError);
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Failed to delete document image',
            show: true,
            errorType: 'error',
          }),
        );
        setDocumentImageLoading(false);
      });
  };

  const updateDocumentImageDisplayOption = (
    imageId: string,
    displayOption: UpdateDocumentImageDisplayOption,
    OnSuccess?: () => void,
  ) => {
    setDocumentImageLoading(true);
    handleRequest(documentImagesUpdateDisplayOption(imageId, displayOption))
      .then(res => {
        if (res) {
          OnSuccess?.();
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Display option updated successfully',
              show: true,
              errorType: 'success',
            }),
          );
        }
        setDocumentImageLoading(false);
      })
      .catch(() => {
        setError(apiError);
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Failed to update display option',
            show: true,
            errorType: 'error',
          }),
        );
        setDocumentImageLoading(false);
      });
  };

  return {
    // Back Numbers
    fetchAdminBacknumberList,
    makeBacknumberAvailable,
    backnumberList,
    loading,
    fetchAllRiderByBackNumber,
    allRiderByBackNumberList,
    setAllRiderByBackNumberList,
    isPopupLoading,
    addRangeBackNumbers,
    isAddBackNumber,
    setIsAddBackNumber,
    backNumberLoading,
    // Document Images
    uploadDocumentImages,
    fetchCommonDocumentImages,
    deleteDocumentImage,
    updateDocumentImageDisplayOption,
    documentImageList,
    commonDocumentImageList,
    documentImageLoading,
    uploadImageLoading,
    setDocumentImageList,
    setCommonDocumentImageList,
  };
};

export default useSettingsApi;